import React from "react";
import { UserPaymentTrancheApi, coursetranchesApi } from "api";
import { PaziaButton, PaziaFormButton, PaziaInput } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { toMoneyFormat } from "utils";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useToaster } from "utils";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";

interface GeneratePaymentLinkModalProps {
  onCloseRequest: () => void;
  isOpen: boolean;
  userId: number;
  courseId: number;
  tranches: UserPaymentTrancheApi[];
  selectedTranche?: UserPaymentTrancheApi;
}

const validationSchema = yup.object().shape({
  amountToPay: yup
    .number()
    .transform((value, originalValue) => {
      // Handle both comma and dot as decimal separators
      if (typeof originalValue === "string") {
        // Replace comma with dot and parse as float
        const normalized = originalValue.replace(",", ".");
        return parseFloat(normalized);
      }
      return value;
    })
    .min(0.01, "Kwota musi być większa niż 0")
    .required("Kwota jest wymagana"),
  selectedTranchesIds: yup
    .array()
    .min(1, "Musisz wybrać przynajmniej jedną transzę")
    .required("Musisz wybrać przynajmniej jedną transzę"),
});

interface FormValues {
  amountToPay: number;
  selectedTranchesIds: number[];
}

export const GeneratePaymentLinkModal = ({
  onCloseRequest,
  isOpen,
  userId,
  courseId,
  tranches,
  selectedTranche,
}: GeneratePaymentLinkModalProps) => {
  const { showError, showSuccess } = useToaster();
  const [generatedLink, setGeneratedLink] = React.useState<string>("");

  // Filter tranches to show only unpaid ones
  const unpaidTranches = tranches.filter(
    (tranche) =>
      tranche.status === 1 || // NOT_PAID
      tranche.status === 2, // PENDING_PAYMENT
  );

  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      amountToPay: selectedTranche?.discountedAmount || 0,
      selectedTranchesIds: selectedTranche ? [selectedTranche.courseTrancheId] : [],
    },
  });

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;
  const selectedTranchesIds = watch("selectedTranchesIds");
  const amountToPay = watch("amountToPay");

  const onSubmit = async (data: FormValues) => {
    try {
      const response = await coursetranchesApi.generatePaymentLink(courseId, {
        userId,
        amountToPay: data.amountToPay,
        selectedTranchesIds: data.selectedTranchesIds,
      });

      setGeneratedLink(response.data.paymentLink);
      showSuccess("Link płatności został wygenerowany");
    } catch (error) {
      console.error(error);
      showError("Wystąpił błąd podczas generowania linku płatności");
    }
  };

  const handleTrancheToggle = (trancheId: number) => {
    const currentIds = selectedTranchesIds || [];
    const newIds = currentIds.includes(trancheId)
      ? currentIds.filter((id) => id !== trancheId)
      : [...currentIds, trancheId];

    setValue("selectedTranchesIds", newIds);

    // Auto-calculate total amount based on selected tranches
    const selectedTranchesTotal = unpaidTranches
      .filter((tranche) => newIds.includes(tranche.courseTrancheId))
      .reduce((sum, tranche) => sum + tranche.discountedAmount, 0);

    setValue("amountToPay", selectedTranchesTotal);
  };

  const handleCloseModal = () => {
    setGeneratedLink("");
    onCloseRequest();
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      showSuccess("Link został skopiowany do schowka");
    } catch (error) {
      showError("Nie udało się skopiować linku");
    }
  };

  if (generatedLink) {
    return (
      <PaziaModal id="generate-payment-link-result-modal" onCloseRequest={handleCloseModal} isOpen={isOpen} size="2xl">
        <ModalPadding>
          <ModalHeader>Link płatności został wygenerowany</ModalHeader>
          <div className="mt-4 space-y-4">
            <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-300">
                Link płatności został pomyślnie utworzony
              </h3>
              <p className="mt-2 text-sm text-green-700 dark:text-green-400">
                Możesz skopiować poniższy link i przesłać go użytkownikowi
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Link płatności:</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={generatedLink}
                  readOnly
                  className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700"
                />
                <PaziaButton btnType="button" type="secondary" onClick={copyToClipboard} title="Kopiuj" />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <PaziaButton btnType="button" type="primary" onClick={handleCloseModal} title="Zamknij" />
            </div>
          </div>
        </ModalPadding>
      </PaziaModal>
    );
  }

  return (
    <PaziaModal id="generate-payment-link-modal" onCloseRequest={handleCloseModal} isOpen={isOpen} size="2xl">
      <ModalPadding>
        <ModalHeader>Generuj link płatności</ModalHeader>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Wybierz transze do opłacenia
                </h3>
                <div className="mt-3 space-y-2">
                  {unpaidTranches.map((tranche) => (
                    <div
                      key={tranche.courseTrancheId}
                      className="flex items-center justify-between rounded-lg border border-slate-200 p-3 dark:border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <label className="flex cursor-pointer items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedTranchesIds?.includes(tranche.courseTrancheId) || false}
                            onChange={() => handleTrancheToggle(tranche.courseTrancheId)}
                            className="text-primary-600 focus:ring-primary-500 rounded border-slate-300 dark:border-slate-600 dark:bg-slate-700"
                          />
                        </label>
                        <div>
                          <div className="font-medium text-slate-900 dark:text-slate-100">{tranche.name}</div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            ID: {tranche.courseTrancheId}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">
                          {toMoneyFormat(tranche.discountedAmount, true)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.selectedTranchesIds && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.selectedTranchesIds.message}</p>
                )}
              </div>

              <div>
                <PaziaInput label="Kwota do zapłacenia" placeholder="0.00" name="amountToPay" />
                {errors.amountToPay && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.amountToPay.message}</p>
                )}
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Kwota automatycznie obliczana na podstawie wybranych transz. Możesz ją zmodyfikować.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <PaziaButton btnType="button" type="secondary" onClick={handleCloseModal} title="Anuluj" />
              <PaziaFormButton allowCleanSubmit type="primary" title="Generuj link" />
            </div>
          </form>
        </FormProvider>
      </ModalPadding>
    </PaziaModal>
  );
};
